/* eslint-disable react-hooks/exhaustive-deps */
import {
	styled,
	Box,
	Card,
	ThemeProvider,
	Theme,
	StyledEngineProvider,
	BoxProps,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StyledTooltip from '../StyledTooltip';
import { useAppDispatch, useAppSelector } from '../../app/Hooks';
import {
	configurationData,
	setIsLastScreenEndUser,
} from '../../redux/SurveySlice';
import { commonLabels } from '../../utils/CommonLabels';
import { STEPTheme, stepMuiTheme } from '../../utils/Theme';
import { ReactComponent as ArrowBlack } from '../../assets/images/Arrow-Black.svg';
import {
	getQuestionnaireData,
	getQuestionnaireDetailsCWAPIStatus,
	getTodoMessage,
	questionnaireData,
	updateResponseJSON,
} from '../../redux/QuestionnarieUserSlice';
import {
	IDashboardQueStatusReqType,
	ITransferUniversalDashBoardData,
	IType,
} from '../../types/CommonTypes';
import GenericConfirmationPopup from '../common/GenericConfirmationPopup';
import GenericModalPopup from '../common/GenericModalPopup';
import { API_STATUS_CONSTANT } from '../../constants/StepConstants';
import { questionnaireAPIKeys } from '../../utils/ApiKeys';
import {
	addPageViewEventForGA,
	getUserAgentBrowser,
	getUserAgentOS,
} from '../../utils/CommonMethods';
import { Numbers } from '../../utils/Enum';
import {
	selectUniversalDashboardResponse,
	selectUniversalDashboardResponseStatus,
	setTransferDashboardDataResponse,
	TransferUniversalDashboardData,
} from '../../redux/TransferUniversalDashboardDataSlice';
import MultiplePageQuestionnaire from './MultiplePageQuestionnaire';
import {
	breakPointDown_768,
	breakPointBetween_480_768,
	breakPointBetween_768_1025,
	breakPointBetween_768_1300,
	breakPointDown_339,
	breakPointDown_480,
} from './common/makeStyles';

declare module '@mui/styles/defaultTheme' {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	interface DefaultTheme extends Theme {}
}

const QuestionnaireName = styled(Box)<BoxProps>({
	fontFamily: 'JohnsonText-Regular',
	fontSize: '25px',
	lineHeight: '32px',
	fontWeight: '700',
	color: STEPTheme.colors.white,
	display: 'flex',
	alignItems: 'center',
	position: 'relative',
	bottom: '50px',
	overflowWrap: 'break-word',
	wordBreak: 'break-word',
	[breakPointDown_768]: {
		position: 'relative',
		right: '14.5vw',
		fontSize: '20px',
		lineHeight: '24px',
	},
	[breakPointBetween_480_768]: {
		position: 'relative',
		right: '4.5vw',
		width: '108%',
	},
	[breakPointDown_480]: {
		right: '11.5vw',
		width: '128%',
	},
	[breakPointDown_339]: {
		fontSize: '18px',
		lineHeight: '20px',
	},
	[breakPointBetween_768_1025]: {
		fontSize: '25px',
		lineHeight: '32px',
	},
});

const IconWrapper = styled('div')({
	position: 'relative',
	width: '47px',
	height: '30px',
	resize: 'both',
});
const IconStyle = styled(ArrowBlack)({
	marginRight: '10px',
	cursor: 'pointer',
});

const QuestionnaireNameBox = styled(Box)({
	backgroundColor: STEPTheme.colors.primary,
	padding: '75px 65px 60px 65px',
	overflow: 'hidden',
});

const StyledCard = styled(Card)({
	flexDirection: 'column',
	display: 'block',
	marginLeft: 'auto',
	marginRight: 'auto',
	marginBottom: '20%',
	marginTop: '-40px',
	borderRadius: '16px',
	padding: '30px',
	paddingTop: '30px',
	paddingBottom: '15vh',
	width: '62%',
	height: 'auto',
	position: 'relative',
	justifyContent: 'center',
	boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.06)',
	overflow: 'visible',
	[breakPointDown_768]: {
		marginLeft: '9.5%',
		marginBottom: '20%',
		marginTop: '-65px',
		paddingTop: '10px',
		width: '65%',
	},
	[breakPointBetween_768_1300]: {
		display: 'flex',
		flexDirection: 'column',
		width: '83.33vw',
		marginLeft: '8.07vw',
		padding: '2vh 0vh 14.9vh 0vh',
	},
});

const StyledBox = styled(Box)({
	margin: '20px',
	alignItems: 'center',
	display: 'flex',
	[breakPointDown_768]: {
		marginLeft: '0px',
		marginRight: '0px',
	},
});

const QuestionnaireMultiplePage = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const questionnaireInfo = useAppSelector(questionnaireData);
	const [questionnaireName, setQuestionnaireName] = useState('');
	const { id: questionnaireId } = useParams();
	const [showPopUp, setShowPopUp] = useState(false);
	const responseJSON = useAppSelector(
		(state) => state.QuestionnaireSurvey.responseJSON
	);
	const questionnaireDetailsAPIStatus = useAppSelector(
		getQuestionnaireDetailsCWAPIStatus
	);
	const [isGenericModalOpen, setIsGenericModalOpen] = useState(false);
	const genericModalMsg = useAppSelector(getTodoMessage);
	const configs = useAppSelector(configurationData);

	// dashboard page entry & exit changes
	const [pageEntryEventRegistered, setPageEntryEventRegistered] =
		useState(false);
	const [
		pageInactivityExitDashboardEventRegistered,
		setPageInactivityExitDashboardEventRegistered,
	] = useState(false);
	const universalTransferDashboardDataResponse = useAppSelector(
		selectUniversalDashboardResponse
	);
	const universalTransferDashboardDataResponseStatus = useAppSelector(
		selectUniversalDashboardResponseStatus
	);
	const recordPageEntryEvent = () => {
		const currLogInUserDetails = JSON.parse(
			`${localStorage.getItem('userDetails')}`
		);
		if (
			currLogInUserDetails?.visitEndTime &&
			currLogInUserDetails?.reActivityEvent !== 'loading'
		) {
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					...JSON.parse(`${localStorage.getItem('userDetails')}`),
					reActivityEvent: 'loading',
				})
			);
			setPageEntryEventRegistered(true);
			const univeralDashboardPayload: ITransferUniversalDashBoardData = {
				apiUrl: `${process.env.REACT_APP_DASHBOARD_API_BASE_URL}Pagevisit/entry`,
				apiType: 'POST',
				apiBody: {
					PageTitle: commonLabels.END_USER,
					PageSubTitle: questionnaireInfo?.data?.questionnaireCode,
					PageURL: window.location.href,
					id: null,
					IdSession: currLogInUserDetails?.idSession ?? null,
					browser: getUserAgentBrowser(),
					os: getUserAgentOS(),
					visitStartTime: new Date().toISOString(),
					visitEndTime: null,
					userEmailId:
						configs?.user?.userEmailId ?? currLogInUserDetails?.userEmailId,
				},
			};
			dispatch(TransferUniversalDashboardData(univeralDashboardPayload));
		}
	};
	const recordInactivityPageExitEvent = () => {
		const currLogInUserDetails = JSON.parse(
			`${localStorage.getItem('userDetails')}`
		);
		if (!currLogInUserDetails?.visitEndTime) {
			setPageInactivityExitDashboardEventRegistered(true);
			dispatch(setTransferDashboardDataResponse(API_STATUS_CONSTANT.IDLE));
			const univeralDashboardPayload: ITransferUniversalDashBoardData = {
				apiUrl: `${process.env.REACT_APP_DASHBOARD_API_BASE_URL}Pagevisit/entry`,
				apiType: 'POST',
				apiBody: {
					PageTitle: commonLabels.END_USER,
					PageSubTitle: questionnaireInfo?.data?.questionnaireCode,
					PageURL: window.location.href,
					id: currLogInUserDetails?.id ?? null,
					IdSession: currLogInUserDetails?.idSession ?? null,
					browser: getUserAgentBrowser(),
					os: getUserAgentOS(),
					visitStartTime: currLogInUserDetails?.visitStartTime ?? null,
					visitEndTime: new Date().toISOString(),
					userEmailId:
						configs?.user?.userEmailId ?? currLogInUserDetails?.userEmailId,
				},
			};
			dispatch(TransferUniversalDashboardData(univeralDashboardPayload));
		}
	};

	const trackUserActivity = () => {
		window.addEventListener('load', resetTimer, true);
		window.addEventListener('mousemove', resetTimer, true);
		window.addEventListener('mousedown', resetTimer, true);
		window.addEventListener('touchstart', resetTimer, true);
		window.addEventListener('touchmove', resetTimer, true);
		window.addEventListener('click', resetTimer, true);
		window.addEventListener('keydown', resetTimer, true);
		window.addEventListener('scroll', resetTimer, true);
		window.addEventListener('wheel', resetTimer, true);
	};

	const removeEventListeners = () => {
		window.removeEventListener('load', resetTimer, true);
		window.removeEventListener('mousemove', resetTimer, true);
		window.removeEventListener('mousedown', resetTimer, true);
		window.removeEventListener('touchstart', resetTimer, true);
		window.removeEventListener('touchmove', resetTimer, true);
		window.removeEventListener('click', resetTimer, true);
		window.removeEventListener('keydown', resetTimer, true);
		window.removeEventListener('scroll', resetTimer, true);
		window.removeEventListener('wheel', resetTimer, true);
	};

	let userEngagementTimeout: NodeJS.Timeout | undefined;
	const resetTimer = () => {
		clearTimeout(userEngagementTimeout);
		//30 secs of inactivity will trigger page exit
		userEngagementTimeout = setTimeout(() => {
			recordInactivityPageExitEvent();
		}, Numbers.thirtyThousand);
		recordPageEntryEvent();
	};

	useEffect(() => {
		trackUserActivity();
		// empty dependency array- for the use effect to be triggered in every render
		// eslint-disable-next-line react-hooks/exhaustive-deps
		() => {
			removeEventListeners();
		};
	}, []);
	// This calls page visit api with current time as start time and no end date
	useEffect(() => {
		const logInUserDetails = JSON.parse(
			`${localStorage.getItem('userDetails')}`
		);

		if (logInUserDetails?.idSession) {
			const univeralDashboardPayload: ITransferUniversalDashBoardData = {
				apiUrl: `${process.env.REACT_APP_DASHBOARD_API_BASE_URL}Pagevisit/entry`,
				apiType: 'POST',
				apiBody: {
					PageTitle: commonLabels.END_USER,
					PageSubTitle: questionnaireInfo?.data?.questionnaireCode,
					PageURL: window.location.href,
					id: null,
					IdSession: logInUserDetails?.idSession ?? null,
					browser: getUserAgentBrowser(),
					os: getUserAgentOS(),
					visitStartTime: new Date().toISOString(),
					visitEndTime: null,
					userEmailId:
						configs?.user?.userEmailId ?? logInUserDetails?.userEmailId,
				},
			};
			dispatch(TransferUniversalDashboardData(univeralDashboardPayload));
		}
	}, []);

	useEffect(() => {
		if (
			universalTransferDashboardDataResponse?.id &&
			universalTransferDashboardDataResponse?.visitStartTime
		) {
			setPageEntryEventRegistered(false);
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					...JSON.parse(`${localStorage.getItem('userDetails')}`),
					visitStartTime:
						universalTransferDashboardDataResponse?.visitStartTime,
					id: universalTransferDashboardDataResponse?.id,
					userEmailId: universalTransferDashboardDataResponse?.userEmailId,
					pageQuestionnaireCodeAsSubTitle:
						questionnaireInfo?.data?.questionnaireCode,
				})
			);
		}
	}, [universalTransferDashboardDataResponse]);

	// Updates local storage with current dashboard api response if already page visit is registered
	useEffect(() => {
		if (
			pageEntryEventRegistered &&
			universalTransferDashboardDataResponseStatus !== 'loading'
		) {
			setPageEntryEventRegistered(false);
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					...JSON.parse(`${localStorage.getItem('userDetails')}`),
					visitStartTime:
						universalTransferDashboardDataResponse?.visitStartTime,
					visitEndTime: null,
					id: universalTransferDashboardDataResponse?.id,
					reActivityEvent: 'loaded',
					pageQuestionnaireCodeAsSubTitle:
						questionnaireInfo?.data?.questionnaireCode,
				})
			);
		}
	}, [
		pageEntryEventRegistered,
		universalTransferDashboardDataResponse,
		universalTransferDashboardDataResponseStatus,
	]);

	useEffect(() => {
		if (
			pageInactivityExitDashboardEventRegistered &&
			universalTransferDashboardDataResponseStatus !== 'loading'
		) {
			setPageInactivityExitDashboardEventRegistered(false);
			if (universalTransferDashboardDataResponse?.visitEndTime) {
				localStorage.setItem(
					'userDetails',
					JSON.stringify({
						...JSON.parse(`${localStorage.getItem('userDetails')}`),
						visitStartTime: null,
						visitEndTime: universalTransferDashboardDataResponse?.visitEndTime,
						id: null,
						reActivityEvent: 'loaded',
					})
				);
			}
		}
	}, [
		pageInactivityExitDashboardEventRegistered,
		universalTransferDashboardDataResponseStatus,
		universalTransferDashboardDataResponse,
	]);
	useEffect(() => {
		if (
			pageEntryEventRegistered &&
			universalTransferDashboardDataResponseStatus !== 'loading'
		) {
			setPageEntryEventRegistered(false);
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					...JSON.parse(`${localStorage.getItem('userDetails')}`),
					visitStartTime:
						universalTransferDashboardDataResponse?.visitStartTime,
					visitEndTime: null,
					id: universalTransferDashboardDataResponse?.id,
					reActivityEvent: 'loaded',
				})
			);
		}
	}, [
		pageEntryEventRegistered,
		universalTransferDashboardDataResponse,
		universalTransferDashboardDataResponseStatus,
	]);

	useEffect(() => {
		const url = new URL(window.location.href);
		const urlArray = url.pathname.split('/');

		addPageViewEventForGA(
			'pageview',
			urlArray.length > Numbers.two
				? `/${urlArray[Numbers.one]}/${urlArray[Numbers.two]}/%`
				: url.pathname,
			questionnaireAPIKeys.MultiPageQuestionnaire
		);
	}, []);
	useEffect(() => {
		const endUserReq: IType = {
			id: questionnaireId ?? '',
		};
		dispatch(getQuestionnaireData(endUserReq));
		const dashboardStatusReq: IDashboardQueStatusReqType = {
			questionnaireId: questionnaireId ?? '',
			questionnaireCode: questionnaireInfo?.data?.questionnaireCode ?? '',
			questionnaireName: questionnaireInfo?.data?.questionnaireName ?? '',
			userEmail: configs?.user?.userEmailId || '',
			browser: getUserAgentBrowser(),
			os: getUserAgentOS(),
			status: commonLabels.incompleteTxt,
			startDate: questionnaireInfo?.data?.startDate ?? '',
			dueDate: questionnaireInfo?.data?.dueDate ?? '',
		};
		dispatch(
			TransferUniversalDashboardData({
				apiUrl: `${process.env.REACT_APP_DASHBOARD_API_BASE_URL}questionnaire/entry`,
				apiType: 'POST',
				apiBody: dashboardStatusReq,
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setQuestionnaireName(questionnaireInfo?.data?.questionnaireName);
	}, [questionnaireInfo]);

	useEffect(() => {
		if (
			questionnaireDetailsAPIStatus ===
			API_STATUS_CONSTANT.ERROR_PERMISSION_DENIED
		) {
			setIsGenericModalOpen(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionnaireDetailsAPIStatus]);

	const goToHome = () => {
		navigate('/');
		navigate(0);
	};

	const handleRedirection = () => {
		setShowPopUp(true);
		dispatch(setIsLastScreenEndUser(true));
	};

	const handleConfirmPopup = () => {
		dispatch(setIsLastScreenEndUser(true));
		if (responseJSON.length > 0) {
			const clearedResponses = responseJSON.map((answer) => ({
				...answer,
				answer: '',
			}));
			clearedResponses.forEach((clearedAnswer) => {
				dispatch(updateResponseJSON(clearedAnswer));
			});
		}
		navigate('/');
		setShowPopUp(false);
	};

	const handleCancelPopup = () => {
		setShowPopUp(false);
	};

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={stepMuiTheme}>
				<Box>
					<QuestionnaireNameBox>
						<QuestionnaireName data-testid="questionnaire-name">
							<StyledTooltip
								title={commonLabels.backArrowTooltip}
								placement="top"
							>
								<IconWrapper>
									<IconStyle
										data-testid="back-arrow"
										onClick={() => {
											handleRedirection();
										}}
									/>
								</IconWrapper>
							</StyledTooltip>
							{questionnaireName}
						</QuestionnaireName>
					</QuestionnaireNameBox>
					<StyledCard raised={true}>
						<StyledBox className="d-flex">
							{questionnaireInfo && (
								<MultiplePageQuestionnaire data-testid="multiple-page-questionnaire"></MultiplePageQuestionnaire>
							)}
						</StyledBox>
					</StyledCard>
					<GenericConfirmationPopup
						open={showPopUp}
						msgBody={commonLabels.questionnaireDataLossMessage}
						title={commonLabels.questionnaireWarningMsg}
						rightBtnText={commonLabels.confirm}
						leftBtnText={commonLabels.cancel}
						isRightBtnVisible={true}
						isLeftBtnVisible={true}
						rightBtnHandler={handleConfirmPopup}
						leftBtnHandler={handleCancelPopup}
						data-testid="confirmation-popup"
					/>

					{isGenericModalOpen && (
						<GenericModalPopup
							openProp={true}
							msgBody={genericModalMsg}
							mainMsg=""
							rightBtnText={'Go To Home'}
							leftBtnText={''}
							isRightBtnVisible={true}
							isLeftBtnVisible={false}
							rightBtnHandler={goToHome}
							leftBtnHandler={() => {}}
							hideCloseBtn={true}
						/>
					)}
				</Box>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

export default QuestionnaireMultiplePage;
